import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Pencil, Trash2 } from 'lucide-react';
import type { ContainerItem } from '../types/import-tracking.types';

interface ContainerItemsTableProps {
  items: ContainerItem[];
  onEdit: (item: ContainerItem) => void;
  onDelete: (itemId: string) => void;
  isLoading: boolean;
  selectedItemId?: string | null;
  onSelect?: (itemId: string) => void;
}

export function ContainerItemsTable({
  items,
  onEdit,
  onDelete,
  isLoading,
  selectedItemId,
  onSelect,
}: ContainerItemsTableProps) {
  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p>No hay items en este contenedor.</p>
        <p className="text-xs mt-1">Agregue items para comenzar el empaquetado.</p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-auto max-h-[400px]">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-6"></TableHead>
            <TableHead>Descripción</TableHead>
            <TableHead className="text-right">Dimensiones (cm)</TableHead>
            <TableHead className="text-right">Peso (kg)</TableHead>
            <TableHead className="text-right">Cant.</TableHead>
            <TableHead className="text-right">Vol. (CBM)</TableHead>
            <TableHead className="text-right">Acciones</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item) => {
            const volumeCbm =
              (item.lengthCm * item.widthCm * item.heightCm * item.quantity) / 1000000;
            const isSelected = selectedItemId === item.id;
            return (
              <TableRow
                key={item.id}
                className={`cursor-pointer ${isSelected ? 'bg-primary/10' : ''}`}
                onClick={() => onSelect?.(item.id)}
              >
                <TableCell>
                  <div
                    className="w-4 h-4 rounded-sm border"
                    style={{ backgroundColor: item.color }}
                  />
                </TableCell>
                <TableCell>
                  <p className="font-medium text-sm">{item.description}</p>
                  {item.material && (
                    <p className="text-xs text-muted-foreground">{item.material.code}</p>
                  )}
                </TableCell>
                <TableCell className="text-right text-xs tabular-nums">
                  {item.lengthCm}x{item.widthCm}x{item.heightCm}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {item.weightKg.toFixed(1)}
                </TableCell>
                <TableCell className="text-right tabular-nums">{item.quantity}</TableCell>
                <TableCell className="text-right tabular-nums">{volumeCbm.toFixed(4)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(item);
                      }}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(item.id);
                      }}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
